(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) :
    typeof define === 'function' && define.amd ? define(['exports'], factory) :
    (global = typeof globalThis !== 'undefined' ? globalThis : global || self, factory(global.FronteggRestApi = {}));
}(this, (function (exports) { 'use strict';

    /*! *****************************************************************************
    Copyright (c) Microsoft Corporation.

    Permission to use, copy, modify, and/or distribute this software for any
    purpose with or without fee is hereby granted.

    THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
    REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
    AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
    INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
    LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
    OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
    PERFORMANCE OF THIS SOFTWARE.
    ***************************************************************************** */

    function __rest(s, e) {
        var t = {};
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
            t[p] = s[p];
        if (s != null && typeof Object.getOwnPropertySymbols === "function")
            for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
                if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                    t[p[i]] = s[p[i]];
            }
        return t;
    }

    function __awaiter(thisArg, _arguments, P, generator) {
        function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
        return new (P || (P = Promise))(function (resolve, reject) {
            function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
            function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
            function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
            step((generator = generator.apply(thisArg, _arguments || [])).next());
        });
    }

    class ContextHolder {
        constructor() {
            this.context = null;
            this.accessToken = null;
            this.user = null;
            this.onRedirectTo = (path) => (window.location.href = path);
            this.logout = () => (window.location.href = '/account/logout');
        }
        static getInstance() {
            if (!ContextHolder.instance) {
                ContextHolder.instance = new ContextHolder();
            }
            return ContextHolder.instance;
        }
        static setContext(context) {
            ContextHolder.getInstance().context = context;
        }
        static setAccessToken(accessToken) {
            ContextHolder.getInstance().accessToken = accessToken;
        }
        static setUser(user) {
            ContextHolder.getInstance().user = user;
        }
        static setOnRedirectTo(onRedirectTo) {
            ContextHolder.getInstance().onRedirectTo = onRedirectTo;
        }
        static setLogout(logout, logoutUrl) {
            ContextHolder.getInstance().logout = (callback) => {
                if (!callback) {
                    ContextHolder.onRedirectTo(logoutUrl);
                }
                else {
                    logout(callback);
                }
            };
        }
        static getContext() {
            var _a;
            return ((_a = ContextHolder.getInstance().context) !== null && _a !== void 0 ? _a : {
                baseUrl: window.location.href,
                tokenResolver: () => 'my-authentication-token',
                logLevel: 'error',
            });
        }
        static getAccessToken() {
            return ContextHolder.getInstance().accessToken;
        }
        static getUser() {
            return ContextHolder.getInstance().user;
        }
        static onRedirectTo(path, opts) {
            return ContextHolder.getInstance().onRedirectTo(path, opts);
        }
        static logout(callback) {
            return ContextHolder.getInstance().logout(callback);
        }
    }
    const FronteggContext = {
        getContext: () => ContextHolder.getContext(),
        getAccessToken: () => ContextHolder.getAccessToken(),
        getUser: () => ContextHolder.getUser(),
        onRedirectTo: (path, opts) => ContextHolder.onRedirectTo(path, opts),
        logout: (callback) => ContextHolder.logout(callback),
    };

    class FronteggApiError extends Error {
        constructor(message, statusCode) {
            super(message);
            this._statusCode = statusCode;
        }
        get statusCode() {
            return this._statusCode;
        }
    }

    function getBaseUrl(context) {
        return __awaiter(this, void 0, void 0, function* () {
            let baseUrl = context.baseUrl;
            const prefix = context.urlPrefix || 'frontegg';
            if (!baseUrl.endsWith('/')) {
                baseUrl += '/';
            }
            if (!baseUrl.endsWith(prefix)) {
                baseUrl += prefix;
            }
            return baseUrl;
        });
    }
    function prepareUrl(context, url, params) {
        return __awaiter(this, void 0, void 0, function* () {
            const baseUrl = yield getBaseUrl(context);
            const paramsToSend = yield buildQueryParams(context, params);
            let finalUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
            const hasKeys = Object.keys(paramsToSend).length > 0;
            if (paramsToSend && hasKeys) {
                const urlParams = new URLSearchParams(paramsToSend);
                finalUrl += `?${urlParams}`;
            }
            return finalUrl;
        });
    }
    function buildRequestHeaders(context, contentType) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const authToken = yield ((_a = context === null || context === void 0 ? void 0 : context.tokenResolver) !== null && _a !== void 0 ? _a : ContextHolder.getAccessToken)();
            const headers = {};
            if (authToken) {
                headers.Authorization = `Bearer ${authToken}`;
            }
            if (contentType) {
                headers['Content-Type'] = contentType;
            }
            for (const additionalHeader of yield getAdditionalHeaders(context)) {
                headers[`${additionalHeader.key}`] = `${additionalHeader.value}`;
            }
            headers['x-frontegg-source'] = 'frontegg-react';
            return headers;
        });
    }
    function buildQueryParams(context, params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!params) {
                params = {};
            }
            const additionalQueryParams = yield getAdditionalQueryParams(context);
            for (const queryParam of additionalQueryParams) {
                params[queryParam.key] = queryParam.value;
            }
            const keys = Object.keys(params);
            for (const key of keys) {
                const value = params[key];
                params[key] = typeof value === 'object' ? JSON.stringify(value) : value;
            }
            return params;
        });
    }
    function getAdditionalQueryParams(context) {
        return __awaiter(this, void 0, void 0, function* () {
            let output = [];
            if (context.additionalQueryParamsResolver) {
                output = yield context.additionalQueryParamsResolver();
            }
            return output;
        });
    }
    function getAdditionalHeaders(context) {
        return __awaiter(this, void 0, void 0, function* () {
            let output = [];
            if (context.additionalHeadersResolver) {
                output = yield context.additionalHeadersResolver();
            }
            return output;
        });
    }
    const sendRequest = (opts) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        const context = ContextHolder.getContext();
        const headers = yield buildRequestHeaders(context, opts.contentType);
        const url = yield prepareUrl(context, opts.url, opts.params);
        const response = yield fetch(url, {
            body: opts.body ? (opts.contentType === 'application/json' ? JSON.stringify(opts.body) : opts.body) : null,
            method: (_a = opts.method) !== null && _a !== void 0 ? _a : 'GET',
            headers: Object.assign(Object.assign({}, headers), opts.headers),
            credentials: (_c = (_b = opts.credentials) !== null && _b !== void 0 ? _b : context.requestCredentials) !== null && _c !== void 0 ? _c : 'same-origin',
        });
        if (response.status === 302) {
            window.location.href = yield response.text();
            return new Promise(() => { });
        }
        if (!response.ok) {
            if (response.status === 413) {
                throw new FronteggApiError('Error request is too large', response.status);
            }
            let errorMessage;
            try {
                errorMessage = yield response.text();
                errorMessage = JSON.parse(errorMessage);
            }
            catch (e) { }
            if (errorMessage.errors) {
                errorMessage = errorMessage.errors.join(', ');
            }
            else if (typeof errorMessage !== 'string') {
                errorMessage = `Error ${response.status} - ${response.statusText}`;
            }
            if (response.status >= 400 && response.status < 500 && ['warn'].includes((_d = context.logLevel) !== null && _d !== void 0 ? _d : ''))
                console.warn(errorMessage);
            else if (response.status === 500 && ['warn', 'error'].includes((_e = context.logLevel) !== null && _e !== void 0 ? _e : ''))
                console.error(errorMessage);
            throw new FronteggApiError(errorMessage, response.status);
        }
        if (!opts.responseType || opts.responseType === 'json') {
            try {
                return yield response.json();
            }
            catch (e) {
                return {};
            }
        }
        else if (opts.responseType === 'blob') {
            const { outputFileName } = opts.params;
            return yield response
                .blob()
                .then((blob) => URL.createObjectURL(blob))
                .then((url) => {
                const tempLink = document.createElement('a');
                tempLink.href = url;
                tempLink.setAttribute('download', outputFileName || 'output');
                tempLink.click();
            });
        }
        else {
            return yield response.text();
        }
    });
    const Get = (url, params, opts) => __awaiter(void 0, void 0, void 0, function* () {
        return sendRequest(Object.assign({ url, method: 'GET', contentType: 'application/json', params }, opts));
    });
    const Post = (url, body, opts) => __awaiter(void 0, void 0, void 0, function* () {
        return sendRequest(Object.assign({ url, method: 'POST', contentType: 'application/json', body }, opts));
    });
    const Patch = (url, body, opts) => __awaiter(void 0, void 0, void 0, function* () {
        return sendRequest(Object.assign({ url, method: 'PATCH', contentType: 'application/json', body }, opts));
    });
    const Put = (url, body, opts) => __awaiter(void 0, void 0, void 0, function* () {
        return sendRequest(Object.assign({ url, method: 'PUT', contentType: 'application/json', body }, opts));
    });
    const Delete = (url, body, opts) => __awaiter(void 0, void 0, void 0, function* () {
        return sendRequest(Object.assign({ url, method: 'DELETE', contentType: 'application/json', body }, opts));
    });
    const PostDownload = (url, body, params, opts) => __awaiter(void 0, void 0, void 0, function* () {
        return sendRequest(Object.assign({ url, method: 'POST', contentType: 'application/json', responseType: 'blob', body,
            params }, opts));
    });

    var fetch$1 = /*#__PURE__*/Object.freeze({
        __proto__: null,
        Get: Get,
        Post: Post,
        Patch: Patch,
        Put: Put,
        Delete: Delete,
        PostDownload: PostDownload
    });

    const urls = {
        vendor: '/vendors',
        identity: {
            auth: {
                v1: '/identity/resources/auth/v1',
                v2: '/identity/resources/auth/v2'
            },
            users: {
                v1: '/identity/resources/users/v1',
                v2: '/identity/resources/users/v2',
                apiTokens: {
                    v1: '/identity/resources/users/api-tokens/v1',
                }
            },
            configurations: {
                v1: '/identity/resources/configurations/v1'
            },
            sso: {
                v1: '/identity/resources/sso/v1'
            },
            permissions: {
                v1: '/identity/resources/permissions/v1'
            },
            tenants: {
                apiTokens: {
                    v1: '/identity/resources/tenants/api-tokens/v1',
                    v2: '/identity/resources/tenants/api-tokens/v2'
                },
                invites: {
                    user: {
                        v1: '/identity/resources/tenants/invites/v1/user'
                    },
                    verify: {
                        v1: '/identity/resources/tenants/invites/v1/verify'
                    },
                    configuration: {
                        v1: '/identity/resources/tenants/invites/v1/configuration'
                    }
                }
            },
            roles: {
                v1: '/identity/resources/roles/v1'
            },
        },
        team: {
            sso: {
                v1: '/team/resources/sso/v1',
                v2: '/team/resources/sso/v2',
            },
            members: {
                v1: '/team/resources/members/v1'
            },
            roles: {
                v1: '/team/resources/roles/v1'
            },
            stats: {
                v1: '/team/resources/stats/v1'
            },
            profile: {
                v1: '/team/resources/profile' // no versioning in backend
            }
        },
        tenants: {
            accountSettings: {
                v1: '/tenants/resources/account-settings/v1'
            }
        },
        integrations: {
            configurations: {
                v1: '/integrations/resources/configurations/v1',
                v2: '/integrations/resources/configurations/v2',
            }
        },
        subscriptions: {
            billing: {
                subscriptions: {
                    v1: '/subscriptions/resources/billing/subscriptions/v1'
                },
                plans: {
                    v1: '/subscriptions/resources/billing/plans/v1'
                },
                invoices: {
                    v1: '/subscriptions/resources/billing/invoices/v1'
                },
                paymentMethods: {
                    v1: '/subscriptions/resources/billing/payment-methods/v1'
                },
                summaries: {
                    v1: '/subscriptions/resources/billing/summaries/v1'
                },
                tenantConfiguration: {
                    v1: '/subscriptions/resources/billing/tenant-configurations/v1'
                },
                paymentProviders: {
                    stripe: {
                        customers: {
                            v1: '/subscriptions/resources/billing/payment-providers/stripe/customers/v1'
                        },
                        publicConfigurations: {
                            v1: '/subscriptions/resources/billing/payment-providers/stripe/public-configurations/v1'
                        },
                        subscriptions: {
                            v1: '/subscriptions/resources/billing/payment-providers/stripe/subscriptions/v1'
                        },
                        setupIntents: {
                            v1: '/subscriptions/resources/billing/payment-providers/stripe/setup-intents/v1'
                        }
                    }
                }
            },
            paymentProviders: {
                v1: '/subscriptions/resources/payment-providers/v1'
            }
        },
        reports: {
            service: {
                v1: '/reports/resources/reports/v1',
                v2: '/reports/resources/reports/v2'
            },
            trigger: {
                v1: '/reports/engine/resources/triggers/v1'
            }
        },
        notifications: {
            v1: '/notification'
        },
        audits: {
            v1: '/audits',
        },
        events: {
            configurations: {
                v1: '/event/resources/configurations/v1'
            }
        },
        webhooks: {
            v1: '/webhook'
        }
    };

    /**
     * Get global secure access configuration
     */
    function getGlobalSecurityPolicy() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getGlobalSecurityPolicy()');
            return Get(urls.identity.configurations.v1);
        });
    }
    /**
     * Get Mfa configuration from security policy
     */
    function getMfaPolicy() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getMfaPolicy()');
            return Get(`${urls.identity.configurations.v1}/mfa-policy`);
        });
    }
    /**
     * Create/Update Mfa configuration from security policy
     */
    function saveMfaPolicy(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('saveMfaPolicy()');
            if (body.id) {
                return Patch(`${urls.identity.configurations.v1}/mfa-policy`, body);
            }
            else {
                return Post(`${urls.identity.configurations.v1}/mfa-policy`, body);
            }
        });
    }
    /**
     * Get Lockout configuration from security policy
     */
    function getLockoutPolicy() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getLockoutPolicy()');
            return Get(`${urls.identity.configurations.v1}/lockout-policy`);
        });
    }
    /**
     * Create/Update Lockout configuration from security policy
     */
    function saveLockoutPolicy(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('saveLockoutPolicy()');
            if (body.id) {
                return Patch(`${urls.identity.configurations.v1}/lockout-policy`, body);
            }
            else {
                return Post(`${urls.identity.configurations.v1}/lockout-policy`, body);
            }
        });
    }
    /**
     * Get Captcha configuration from security policy
     */
    function getCaptchaPolicy() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield Get(`${urls.identity.configurations.v1}/captcha-policy/public`);
            }
            catch (_a) {
                return null;
            }
        });
    }
    /**
     * Get Password History configuration from security policy
     */
    function getPasswordHistoryPolicy() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getPasswordHistoryPolicy()');
            return Get(`${urls.identity.configurations.v1}/password-history-policy`);
        });
    }
    /**
     * Create/Update Password History configuration from security policy
     */
    function savePasswordHistoryPolicy(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('savePasswordHistoryPolicy()');
            if (body.id) {
                return Patch(`${urls.identity.configurations.v1}/password-history-policy`, body);
            }
            else {
                return Post(`${urls.identity.configurations.v1}/password-history-policy`, body);
            }
        });
    }
    /**
     * load vendor password configuration.
     */
    function getPasswordConfigPolicy() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getPasswordConfigPolicy()');
            return Get(`${urls.identity.configurations.v1}/password`);
        });
    }

    exports.SocialLoginProviders = void 0;
    (function (SocialLoginProviders) {
        SocialLoginProviders["Microsoft"] = "microsoft";
        SocialLoginProviders["Facebook"] = "facebook";
        SocialLoginProviders["Google"] = "google";
        SocialLoginProviders["Github"] = "github";
    })(exports.SocialLoginProviders || (exports.SocialLoginProviders = {}));
    exports.AuthStrategyEnum = void 0;
    (function (AuthStrategyEnum) {
        AuthStrategyEnum["EmailAndPassword"] = "EmailAndPassword";
        AuthStrategyEnum["MagicLink"] = "MagicLink";
        AuthStrategyEnum["Code"] = "Code";
        AuthStrategyEnum["NoLocalAuthentication"] = "NoLocalAuthentication";
    })(exports.AuthStrategyEnum || (exports.AuthStrategyEnum = {}));

    /**
     * The code was extracted from:
     * https://github.com/davidchambers/Base64.js
     */
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    class InvalidCharacterError extends Error {
        constructor(message) {
            super(message);
        }
    }
    function polyfill(input) {
        const str = String(input).replace(/=+$/, '');
        if (str.length % 4 === 1) {
            throw new InvalidCharacterError("'atob' failed: The string to be decoded is not correctly encoded.");
        }
        let output = '';
        // noinspection JSAssignmentUsedAsCondition,JSUnusedAssignment,CommaExpressionJS
        for (
        // initialize result and counters
        let bc = 0, bs, buffer, idx = 0; 
        // get next character
        // tslint:disable-next-line:no-conditional-assignment
        (buffer = str.charAt(idx++)); 
        // character found in table? initialize bit storage and add its ascii value;
        // tslint:disable-next-line:no-conditional-assignment
        ~buffer &&
            ((bs = bc % 4 ? bs * 64 + buffer : buffer),
                // and if not first of each 4 characters,
                // convert the first 8 bits to one ascii character
                bc++ % 4)
            ? (output += String.fromCharCode(255 & (bs >> ((-2 * bc) & 6))))
            : 0) {
            // try to find character in table (0-63, not found => -1)
            buffer = chars.indexOf(buffer);
        }
        return output;
    }
    const atob = (typeof window !== 'undefined' && window.atob && window.atob.bind(window)) || polyfill;
    function b64DecodeUnicode(str) {
        return decodeURIComponent(atob(str).replace(/(.)/g, (m, p) => {
            let code = p.charCodeAt(0).toString(16).toUpperCase();
            if (code.length < 2) {
                code = '0' + code;
            }
            return '%' + code;
        }));
    }
    const base64UrlDecode = (str) => {
        let output = str.replace(/-/g, '+').replace(/_/g, '/');
        switch (output.length % 4) {
            case 0:
                break;
            case 2:
                output += '==';
                break;
            case 3:
                output += '=';
                break;
            default:
                throw new Error('Illegal base64url string!');
        }
        try {
            return b64DecodeUnicode(output);
        }
        catch (err) {
            return atob(output);
        }
    };
    class InvalidTokenError extends Error {
        constructor(message) {
            super(message);
        }
    }
    const jwtDecode = (token, options = {}) => {
        const pos = !!options.header ? 0 : 1;
        try {
            return JSON.parse(base64UrlDecode(token.split('.')[pos]));
        }
        catch (e) {
            throw new InvalidTokenError('Invalid token specified: ' + e.message);
        }
    };

    /*****************************************
     * Authentication
     *****************************************/
    function generateLoginResponse(loginResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!loginResponse.accessToken) {
                return loginResponse;
            }
            ContextHolder.setAccessToken(loginResponse.accessToken);
            const me = yield Get(`${urls.identity.users.v2}/me`);
            const decodedContent = loginResponse.accessToken ? jwtDecode(loginResponse.accessToken) : {};
            const user = Object.assign(Object.assign(Object.assign({}, loginResponse), decodedContent), me);
            ContextHolder.setUser(user);
            return user;
        });
    }
    /**
     * Check if requested email address has sso configuration
     * If true, this function will return the sso address to navigate to
     * else, return null
     */
    function preLogin(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.info('preLogin()', body);
            try {
                const { address } = yield Post(`${urls.identity.auth.v1}/user/saml/prelogin`, body);
                return address;
            }
            catch (_a) {
                return null;
            }
        });
    }
    /**
     * Check if requested email address has sso configuration
     * If true, this function will return the sso address and IdpType to navigate to
     */
    function preLoginWithIdpType(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.info('preLoginWithIdpType()', body);
            return Post(`${urls.identity.auth.v1}/user/saml/prelogin`, body);
        });
    }
    /**
     * After calling preLogin and navigated to SSO url, the IDP will redirect you to the ACS Url
     * Which configured in the SSO configuraation
     * else, return null
     */
    function postLogin(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('postLogin()');
            const data = yield Post(`${urls.identity.auth.v1}/user/saml/postlogin`, body);
            return generateLoginResponse(data);
        });
    }
    /**
     * After calling preLogin and navigated to SSO url, the IDP will redirect you to the redirect url
     * with code and state
     */
    function oidcPostLogin(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('oidcPostLogin()');
            const data = yield Post(`${urls.identity.auth.v1}/user/oidc/postlogin`, body);
            return generateLoginResponse(data);
        });
    }
    /**
     * login with username and password.
     * if the user has two factor authentication
     *    the server will return mfaToken with mfaRequired: true,
     *    and then ``loginWithMfa`` should be called with the mfaToken and and generated code
     * else, the server will accessToken and refreshToken.
     * the refresh should be used to renew your access token by calling ``refreshToken``
     *
     * @throw exception if login failed
     */
    function login(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('login()');
            const data = yield Post(`${urls.identity.auth.v1}/user`, body);
            return generateLoginResponse(data);
        });
    }
    /**
     * after login succeeded with mfaRequired token response, this function should be called
     * with the mfaToken and the generated code from your authenticator app.
     *
     * @throw exception if generated code or mfaToken are incorrect
     */
    function loginWithMfa(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('loginWithMfa()');
            const data = yield Post(`${urls.identity.auth.v1}/user/mfa/verify`, body);
            return generateLoginResponse(data);
        });
    }
    /**
     * activating account should be called after registering new user of deactivate account
     * ``activateAccount`` should contains userId and the token that has been sent to the user after activation requested.
     *
     * @throws exception if activation failed
     */
    function activateAccount(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('activateAccount()');
            return Post(`${urls.identity.users.v1}/activate`, body);
        });
    }
    /**
     * get account activation configuration.
     */
    function getActivateAccountStrategy(params) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getActivateAccountStrategy()');
            return Get(`${urls.identity.users.v1}/activate/strategy`, params);
        });
    }
    /**
     * resend activation email should be called after a failed user activation.
     * ``resend activation email`` should contain  the user email.
     *
     * @throws exception if resend failed
     */
    function resendActivationEmail(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('resendActivationEmail()');
            return Post(`${urls.identity.users.v1}/activate/reset`, body);
        });
    }
    /**
     * activating account should be called after registering new user of deactivate account
     * ``activateAccount`` should contains userId and the token that has been sent to the user after activation requested.
     *
     * @throws exception if activation failed
     */
    function acceptInvitation(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('acceptInvitation()');
            return Post(`${urls.identity.users.v1}/invitation/accept`, body);
        });
    }
    /**
     * refresh token called as authenticated use, access and refresh tokens resolved by the cookies.
     * the server will return ILoginResponse with new access Token and refresh token and store it in the browser cookies.
     */
    function refreshToken() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('refreshToken()');
            const data = yield Post(`${urls.identity.auth.v1}/user/token/refresh`);
            return generateLoginResponse(data);
        });
    }
    /**
     * logout from server, invalidate access and refresh token, remove it from cookies.
     */
    function logout() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('logout()');
            return Post(`${urls.identity.auth.v1}/logout`);
        });
    }
    /**
     * calling forgot password request will send email with link to reset user's password.
     *
     * @throws exception if the user not found
     */
    function forgotPassword(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('forgotPassword()', body);
            return Post(`${urls.identity.users.v1}/passwords/reset`, body);
        });
    }
    /**
     * reset password should be called after forget password request.
     * userId, new password and the token has been sent to the user email.
     *
     * @throws exception if the user not found, password validation failed or invalid token.
     */
    function resetPassword(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('resetPassword()');
            return Post(`${urls.identity.users.v1}/passwords/reset/verify`, body);
        });
    }
    /**
     * load password configuration for user.
     */
    function loadPasswordConfig(params) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('loadPasswordConfig()');
            return Get(`${urls.identity.users.v1}/passwords/config`, params);
        });
    }
    /**
     * recover Multi-Factor authentication by providing the recoveryCode
     * that has been received when activated it
     *
     * @throws exception if recovery code is not valid
     */
    function recoverMfaToken(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('recoverMfaToken()', body);
            return Post(`${urls.identity.auth.v1}/user/mfa/recover`, body);
        });
    }
    /**
     * Multi-Factor Settings
     */
    /**
     * enroll Multi-Factor Auth Code to use in 3rd party apps like Google Authenticator.
     * the server returns qrCode as png image in base64 format
     *
     * @throws exception if the user already have mfa-enabled
     * ``authorized user``
     */
    function enrollMfa() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('enrollMfa()');
            return Post(`${urls.identity.users.v1}/mfa/enroll`);
        });
    }
    /**
     * after enrolling MFA QR code, verify function should be called
     * with the generated code in the Authenticator App
     *
     * @return recoveryCode to use to disable mfa if your device is lost, this code won't show it again.
     * @throws exception if the generated token is invalid
     * ``authorized user``
     */
    function verifyMfa(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('verifyMfa()', body);
            return Post(`${urls.identity.users.v1}/mfa/enroll/verify`, body);
        });
    }
    /**
     * disable MFA by either passing the recoveryCode or the generated code from the Authenticator App.
     *
     * @throws exception if the generated token or the recoveryCode are incorrect.
     * * ``authorized user``
     */
    function disableMfa(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('disableMfa()', body);
            return Post(`${urls.identity.users.v1}/mfa/disable`, body);
        });
    }
    /**
     * SSO Configurations
     */
    /**
     *  Retrieve SAML configurations from server by logged in user (tenantId, vendorId)
     *
     *  @return {enabled: false} if no saml configuration found. else ISamlConfiguration
     * * ``authorized user``
     */
    function getSamlConfiguration() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSamlConfiguration()');
            return Get(`${urls.team.sso.v1}/saml/configurations`);
        });
    }
    /**
     *  Update SAML configuration by logged in user (tenantId, vendorId)
     * * ``authorized user``
     */
    function updateSamlConfiguration(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateSamlConfiguration()', body);
            return Post(`${urls.team.sso.v1}/saml/configurations`, body);
        });
    }
    /**
     *  Retrieve 'Vendor' Saml config from server by logged in user (tenantId, vendorId)
     *
     *  @throws exception 'ACS url information not found' if no saml vendor configuration found.
     * * ``authorized user``
     */
    function getSamlVendorConfiguration() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSamlVendorConfiguration()');
            return Get(`${urls.team.sso.v1}/saml/configurations/vendor-config`);
        });
    }
    /**
     *  Update Vendor Saml metadata by logged in user (tenantId, vendorId)
     *
     *  @return {enabled: false} if no saml configuration found. else ISamlConfiguration
     * * ``authorized user``
     */
    function updateSamlVendorMetadata(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateSamlVendorMetadata()', body);
            return Put(`${urls.team.sso.v1}/saml/configurations/metadata`, body);
        });
    }
    /**
     *  Validate Saml configuration's domain by logged in user (tenantId, vendorId)
     *
     *  @return {enabled: false} if no saml configuration found. else ISamlConfiguration
     * * ``authorized user``
     */
    function validateSamlDomain() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('validateSamlDomain()');
            return Put(`${urls.team.sso.v1}/saml/validations/domain`);
        });
    }
    /**
     *  Get Saml roles for authorization
     * @return array of role IDs
     */
    function getSamlRoles() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSamlRoles()');
            return Get(`${urls.team.sso.v1}/saml/configurations/roles/default`);
        });
    }
    /**
     *  Get Saml roles groups
     * @return array of groups and assigend role IDs
     */
    function getSamlRolesGroups() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSamlRoles()');
            return Get(`${urls.team.sso.v2}/saml/configurations/groups`);
        });
    }
    /**
     *  Update Saml roles for authorization
     */
    function updateSamlRoles({ roleIds }) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateSamlRoles()');
            return Post(`${urls.team.sso.v1}/saml/configurations/roles/default`, { roleIds });
        });
    }
    /**
     *  Create Saml group roles for authorization
     */
    function createSamlGroup({ roleIds, group }) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('createSamlGroup()');
            return Post(`${urls.team.sso.v2}/saml/configurations/groups`, { group, roleIds });
        });
    }
    /**
     *  Update Saml group roles for authorization
     */
    function updateSamlGroup({ roleIds, group, id }) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateSamlGroup()');
            return Patch(`${urls.team.sso.v2}/saml/configurations/groups/${id}`, { group, roleIds });
        });
    }
    /**
     *  Delete Saml group
     */
    function deleteSamlGroup({ id }) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('deleteSamlGroup()');
            return Delete(`${urls.team.sso.v2}/saml/configurations/groups/${id}`);
        });
    }
    /**
     *  Retrieve OIDC configurations from server by logged in user (vendorId)
     *
     *  @return {active: true} if no oidc configuration found. else IOidcConfiguration
     * * ``authorized user``
     */
    function getOidcConfiguration() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getOidcConfiguration()');
            return Get(`${urls.team.sso.v1}/oidc/configurations`);
        });
    }
    /**
     *  Check if one or more saml/oidc configuration are active
     *
     **  @return {isActive: true/false}
     */
    function getSSOPublicConfiguration() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSSOPublicConfiguration()');
            return Get(`${urls.team.sso.v2}/configurations/public`);
        });
    }
    /**
     *  Get social logins providers configurations for vendor
     * @return array of providers configurations
     */
    function getSocialLoginProviders() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSocialLoginProviders()');
            return Get(urls.identity.sso.v1);
        });
    }
    /**
     * Login using social login
     * @return cookie with refresh token
     */
    function loginViaSocialLogin({ provider, code, redirectUri, codeVerifier, metadata, invitationToken, }) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('loginViaSocialLogin()');
            const params = { code };
            if (redirectUri) {
                params.redirectUri = redirectUri;
            }
            if (codeVerifier) {
                params.code_verifier = codeVerifier;
            }
            return Post(`${urls.identity.auth.v1}/user/sso/${provider}/postlogin`, { metadata, invitationToken }, { params });
        });
    }
    /**
     * Get vendor secure access configuration
     */
    function getVendorConfig() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getVendorConfig()');
            return Get(`${urls.identity.configurations.v1}/public`);
        });
    }
    /**
     * Sign up new user
     * create new user with a new tenant
     */
    function signUpUser(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('signUpUser()', body);
            const { shouldActivate, authResponse } = yield Post(`${urls.identity.users.v1}/signUp`, body);
            const loginResponse = !shouldActivate && authResponse ? yield generateLoginResponse(authResponse) : undefined;
            return { shouldActivate, user: loginResponse };
        });
    }
    /**
     * Api tokens Configurations
     */
    /**
     * Get user api tokens data
     */
    function getUserApiTokensData() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getUserApiTokensData()');
            return Get(urls.identity.users.apiTokens.v1);
        });
    }
    /**
     * Get tenant api tokens data
     */
    function getTenantApiTokensData() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('geTenantApiTokensData()');
            return Get(urls.identity.tenants.apiTokens.v1);
        });
    }
    /**
     *  Update User Api Tokens
     */
    function updateUserApiTokensData(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateUserApiTokensData()', body);
            return Post(urls.identity.users.apiTokens.v1, body);
        });
    }
    /**
     * Update Tenant Api Tokens
     */
    function updateTenantApiTokensData(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateTenantApiTokensData()', body);
            return Post(urls.identity.tenants.apiTokens.v2, body);
        });
    }
    /**
     * Delete Tenant Api Token
     */
    function deleteTenantApiToken({ tokenId }) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('deleteTenantApiToken()', tokenId);
            return Delete(`${urls.identity.tenants.apiTokens.v1}/${tokenId}`);
        });
    }
    /**
     * Delete Tenant Api Token
     */
    function deleteUserApiToken({ tokenId }) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('deleteUserApiToken()', tokenId);
            return Delete(`${urls.identity.users.apiTokens.v1}/${tokenId}`);
        });
    }
    /**
     * Get Tenant Api Token Creator User
     */
    function getUserById({ userId }) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getUserById()');
            return Get(`${urls.identity.users.v1}/${userId}`);
        });
    }
    /**
     * Checks if remember MFA device is enabled for user.
     */
    function checkIfAllowToRememberMfaDevice(mfaToken) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('checkIfAllowToRememberMfaDevice()');
            return Get(`${urls.identity.configurations.v1}/mfa-policy/allow-remember-device`, { mfaToken });
        });
    }
    /**
     * Passwordless prelogin authentication
     */
    function passwordlessPreLogin(_a) {
        var { type } = _a, body = __rest(_a, ["type"]);
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('passwordlessPrelogin()');
            return Post(`${urls.identity.auth.v1}/passwordless/${type.toLocaleLowerCase()}/prelogin`, body);
        });
    }
    function passwordlessPostLogin(_a) {
        var { type } = _a, body = __rest(_a, ["type"]);
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('passwordlessPostLogin()');
            return Post(`${urls.identity.auth.v1}/passwordless/${type.toLocaleLowerCase()}/postlogin`, body);
        });
    }
    /**
     * Check if tenant invitation token is valid
     * @param token - invitation token
     * @returns tenant name
     */
    function verifyInviteToken({ token }) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('verifyInviteToken()');
            return Post(urls.identity.tenants.invites.verify.v1, { token });
        });
    }
    /**
     * SSO Configurations V1
     */
    /**
     *  Retrieve SSO configurations from server by logged in user (tenantId, vendorId)
     *
     *  @return ISSOConfiguration[]
     * * ``authorized user``
     */
    function getSSOConfigurations() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSSOConfigurations()');
            return Get(`${urls.team.sso.v1}/configurations`);
        });
    }
    /**
     *  Create SSO configuration by logged in user (tenantId, vendorId)
     *
     *  @returns ISSOConfiguration
     * * ``authorized user``
     */
    function createSSOConfiguration(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('createSSOConfiguration()', body);
            return Post(`${urls.team.sso.v1}/configurations`, body);
        });
    }
    /**
     *  Update SSO configuration by logged in user (tenantId, vendorId)
     *
     *  @returns ISSOConfiguration
     * * ``authorized user``
     */
    function updateSSOConfiguration(ssoConfigId, body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateSSOConfiguration()', { body, ssoConfigId });
            return Patch(`${urls.team.sso.v1}/configurations/${ssoConfigId}`, body);
        });
    }
    /**
     *  Delete SSO configuration by logged in user (tenantId, vendorId)
     * * ``authorized user``
     */
    function deleteSSOConfiguration(ssoConfigId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('deleteSSOConfiguration()', { ssoConfigId });
            return Delete(`${urls.team.sso.v1}/configurations/${ssoConfigId}`);
        });
    }
    /**
     *  Create SSO configuration by metadata by logged in user (tenantId, vendorId)
     *
     *  @returns ISSOConfiguration
     * * ``authorized user``
     */
    function createSSOConfigurationByMetadata(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('createSSOConfigurationByMetadata()', body);
            return Post(`${urls.team.sso.v1}/configurations/metadata`, body);
        });
    }
    /**
     *  Update SSO configuration by metadata by logged in user (tenantId, vendorId)
     *
     *  @returns ISSOConfiguration
     * * ``authorized user``
     */
    function updateSSOConfigurationByMetadata(ssoConfigId, body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateSSOConfigurationByMetadata()', { body, ssoConfigId });
            return Put(`${urls.team.sso.v1}/configurations/${ssoConfigId}/metadata`, body);
        });
    }
    /**
     *  Create SSO configuration domain by logged in user (tenantId, vendorId)
     *
     *  @returns ISSODomain
     * * ``authorized user``
     */
    function createSSODomain(ssoConfigId, body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('createSSODomain()', { body, ssoConfigId });
            return Post(`${urls.team.sso.v1}/configurations/${ssoConfigId}/domains`, body);
        });
    }
    /**
     *  Create SSO configuration domain by logged in user (tenantId, vendorId)
     * * ``authorized user``
     */
    function deleteSSODomain(ssoConfigId, domainId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('deleteSSODomain()', { domainId, ssoConfigId });
            return Delete(`${urls.team.sso.v1}/configurations/${ssoConfigId}/domains/${domainId}`);
        });
    }
    /**
     *  Validate SSO configuration domain by logged in user (tenantId, vendorId)
     * * ``authorized user``
     */
    function validateSSODomain(ssoConfigId, domainId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('validateSSODomain()', { domainId, ssoConfigId });
            return Put(`${urls.team.sso.v1}/configurations/${ssoConfigId}/domains/${domainId}/validate`);
        });
    }
    /**
     *  Get SSO configuration default roles by logged in user (tenantId, vendorId)
     * * ``authorized user``
     */
    function getSSODefaultRoles(ssoConfigId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSSODefaultRoles()', { ssoConfigId });
            return Get(`${urls.team.sso.v1}/configurations/${ssoConfigId}/roles`);
        });
    }
    /**
     *  Create SSO configuration default roles by logged in user (tenantId, vendorId)
     * * ``authorized user``
     */
    function setSSODefaultRoles(ssoConfigId, body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('setSSODefaultRoles()', { body, ssoConfigId });
            return Put(`${urls.team.sso.v1}/configurations/${ssoConfigId}/roles`, body);
        });
    }
    /**
     *  Create SSO configuration group roles for authorization
     */
    function createSSOGroup(ssoConfigId, body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('createSSOGroup()', { body, ssoConfigId });
            return Post(`${urls.team.sso.v1}/configurations/${ssoConfigId}/groups`, body);
        });
    }
    /**
     *  Update SSO configuration group roles for authorization
     */
    function updateSSOGroup(ssoConfigId, { roleIds, group, id }) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateSSOGroup()', { ssoConfigId, roleIds, group });
            return Patch(`${urls.team.sso.v1}/configurations/${ssoConfigId}/groups/${id}`, { group, roleIds });
        });
    }
    /**
     *  Delete SSO configuration group
     */
    function deleteSSOGroup(ssoConfigId, groupId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('deleteSSOGroup()', { ssoConfigId, groupId });
            return Delete(`${urls.team.sso.v1}/configurations/${ssoConfigId}/groups/${groupId}`);
        });
    }
    /**
     *  Get SSO configuration groups
     */
    function getSSOGroups(ssoConfigId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSSOGroups()', { ssoConfigId });
            return Get(`${urls.team.sso.v1}/configurations/${ssoConfigId}/groups`);
        });
    }
    /**
     * Check if requested email address has sso configuration
     * If true, this function will return the sso address to navigate to
     * else, return null
     */
    function preLoginV2(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.info('preLoginV2()', body);
            return Post(`${urls.identity.auth.v2}/user/sso/prelogin`, body);
        });
    }
    /**
     * After calling preLogin and navigated to SSO url, the IDP will redirect you to the redirect url
     * with code and state
     */
    function oidcPostLoginV2(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('oidcPostLoginV2()');
            const data = yield Post(`${urls.identity.auth.v2}/user/oidc/postlogin`, body);
            return generateLoginResponse(data);
        });
    }

    var auth = /*#__PURE__*/Object.freeze({
        __proto__: null,
        generateLoginResponse: generateLoginResponse,
        preLogin: preLogin,
        preLoginWithIdpType: preLoginWithIdpType,
        postLogin: postLogin,
        oidcPostLogin: oidcPostLogin,
        login: login,
        loginWithMfa: loginWithMfa,
        activateAccount: activateAccount,
        getActivateAccountStrategy: getActivateAccountStrategy,
        resendActivationEmail: resendActivationEmail,
        acceptInvitation: acceptInvitation,
        refreshToken: refreshToken,
        logout: logout,
        forgotPassword: forgotPassword,
        resetPassword: resetPassword,
        loadPasswordConfig: loadPasswordConfig,
        recoverMfaToken: recoverMfaToken,
        enrollMfa: enrollMfa,
        verifyMfa: verifyMfa,
        disableMfa: disableMfa,
        getSamlConfiguration: getSamlConfiguration,
        updateSamlConfiguration: updateSamlConfiguration,
        getSamlVendorConfiguration: getSamlVendorConfiguration,
        updateSamlVendorMetadata: updateSamlVendorMetadata,
        validateSamlDomain: validateSamlDomain,
        getSamlRoles: getSamlRoles,
        getSamlRolesGroups: getSamlRolesGroups,
        updateSamlRoles: updateSamlRoles,
        createSamlGroup: createSamlGroup,
        updateSamlGroup: updateSamlGroup,
        deleteSamlGroup: deleteSamlGroup,
        getOidcConfiguration: getOidcConfiguration,
        getSSOPublicConfiguration: getSSOPublicConfiguration,
        getSocialLoginProviders: getSocialLoginProviders,
        loginViaSocialLogin: loginViaSocialLogin,
        getVendorConfig: getVendorConfig,
        signUpUser: signUpUser,
        getUserApiTokensData: getUserApiTokensData,
        getTenantApiTokensData: getTenantApiTokensData,
        updateUserApiTokensData: updateUserApiTokensData,
        updateTenantApiTokensData: updateTenantApiTokensData,
        deleteTenantApiToken: deleteTenantApiToken,
        deleteUserApiToken: deleteUserApiToken,
        getUserById: getUserById,
        checkIfAllowToRememberMfaDevice: checkIfAllowToRememberMfaDevice,
        passwordlessPreLogin: passwordlessPreLogin,
        passwordlessPostLogin: passwordlessPostLogin,
        verifyInviteToken: verifyInviteToken,
        getSSOConfigurations: getSSOConfigurations,
        createSSOConfiguration: createSSOConfiguration,
        updateSSOConfiguration: updateSSOConfiguration,
        deleteSSOConfiguration: deleteSSOConfiguration,
        createSSOConfigurationByMetadata: createSSOConfigurationByMetadata,
        updateSSOConfigurationByMetadata: updateSSOConfigurationByMetadata,
        createSSODomain: createSSODomain,
        deleteSSODomain: deleteSSODomain,
        validateSSODomain: validateSSODomain,
        getSSODefaultRoles: getSSODefaultRoles,
        setSSODefaultRoles: setSSODefaultRoles,
        createSSOGroup: createSSOGroup,
        updateSSOGroup: updateSSOGroup,
        deleteSSOGroup: deleteSSOGroup,
        getSSOGroups: getSSOGroups,
        preLoginV2: preLoginV2,
        oidcPostLoginV2: oidcPostLoginV2,
        getGlobalSecurityPolicy: getGlobalSecurityPolicy,
        getMfaPolicy: getMfaPolicy,
        saveMfaPolicy: saveMfaPolicy,
        getLockoutPolicy: getLockoutPolicy,
        saveLockoutPolicy: saveLockoutPolicy,
        getCaptchaPolicy: getCaptchaPolicy,
        getPasswordHistoryPolicy: getPasswordHistoryPolicy,
        savePasswordHistoryPolicy: savePasswordHistoryPolicy,
        getPasswordConfigPolicy: getPasswordConfigPolicy,
        get SocialLoginProviders () { return exports.SocialLoginProviders; },
        get AuthStrategyEnum () { return exports.AuthStrategyEnum; }
    });

    /*****************************************
     * Profile Api
     *****************************************/
    /**
     * change user password by providing current password and the new password.
     *
     * @throws exception if the current password incorrect or new password validation failed.
     * ``authorized user``
     */
    function getProfile(params) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getProfile()');
            return Get(`${urls.identity.users.v2}/me`, params !== null && params !== void 0 ? params : { addRoles: true });
        });
    }
    /**
     * update user profile by providing updated fields.
     * ``authorized user``
     */
    function updateProfile(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateProfile()', body);
            return Put(`${urls.identity.users.v2}/me`, body);
        });
    }
    /**
     * update user profile image
     * ``authorized user``
     */
    function updateProfileImage(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateProfileImage()', body);
            return Put(`${urls.team.profile.v1}/me/image/v1`, body, { responseType: 'plain', contentType: undefined });
        });
    }
    /**
     * change user password by providing current password and the new password.
     *
     * @throws exception if the current password incorrect or new password validation failed.
     * ``authorized user``
     */
    function changePassword(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('changePassword()');
            return Post(`${urls.identity.users.v1}/passwords/change`, body);
        });
    }
    /*****************************************
     * Team Api
     *****************************************/
    /**
     * load users from team-management-service
     *
     * ``authorized user``
     */
    function loadUsers(params) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            const filters = (params.filter || []).reduce((p, n) => (Object.assign(Object.assign({}, p), { [n.id]: n.value })), {});
            const sorts = ((_a = params.sort) === null || _a === void 0 ? void 0 : _a.length)
                ? {
                    sortBy: (_d = (_c = (_b = params.sort) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : 'name',
                    sortDirection: ((_f = (_e = params.sort) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.desc) ? 'desc' : 'asc',
                }
                : null;
            return Get(urls.team.members.v1, Object.assign(Object.assign({ pageOffset: params.pageOffset, pageSize: params.pageSize }, filters), sorts));
        });
    }
    /**
     * add user to team, invitation link will be sent to the added user.
     *
     * ``authorized user``
     */
    function addUser(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('addUser()', body);
            return Post(urls.identity.users.v2, Object.assign(Object.assign({}, body), { provider: 'local' }));
        });
    }
    /**
     * delete user from team, user will be deleted.
     *
     * ``authorized user``
     */
    function deleteUser(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('deleteUser()', body);
            return Delete(`${urls.team.members.v1}/${body.userId}`);
        });
    }
    /**
     * edit user roles in team
     *
     * ``authorized user``
     */
    function updateUser(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('editUser()', body);
            return Put(urls.team.members.v1, body);
        });
    }
    /**
     * create link to invite user
     *
     * ``authorized user``
     */
    function createInviteUserLink(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('createInviteUserLink()');
            return Post(urls.identity.tenants.invites.user.v1, Object.assign({}, body));
        });
    }
    /**
     * update link to invite user
     *
     * ``authorized user``
     */
    function updateInviteUserLink(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateInviteUserLink()', body);
            return Patch(urls.identity.tenants.invites.user.v1, Object.assign({}, body));
        });
    }
    /**
     * delete link to invite user
     *
     * ``authorized user``
     */
    function deleteInviteUserLink() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('deleteInviteUserLink()');
            return Delete(urls.identity.tenants.invites.user.v1);
        });
    }
    /**
     * get link to invite user
     *
     * ``authorized user``
     */
    function getInviteUserLink() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getInviteUserLink()');
            return Get(urls.identity.tenants.invites.user.v1);
        });
    }
    /**
     * get invite link configuration
     *
     */
    function getInviteLinkConfiguration() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getInviteLinkConfiguration()');
            return Get(urls.identity.tenants.invites.configuration.v1);
        });
    }
    /**
     * load available roles that can be assigned to team users from team-management-service
     *
     * ``authorized user``
     */
    function loadAvailableRoles() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('loadAvailableRoles()');
            return Get(urls.team.roles.v1);
        });
    }
    function loadAvailablePermissions() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('loadAvailablePermissions()');
            return Get(urls.identity.permissions.v1);
        });
    }
    /**
     * load users stats from team-management-service
     *
     * ``authorized user``
     */
    function loadStats() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('loadStats()');
            return Get(`${urls.team.stats.v1}/members`);
        });
    }
    /**
     * resend activation link to specific user
     *
     * ``authorized user``
     */
    function resendActivationLink(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('resendActivationLink()', body);
            return Post(`${urls.team.members.v1}/${body.userId}/resendActivationEmail`, {});
        });
    }
    /**
     * resend accept join tenant invitation link to specific user
     *
     * ``authorized user``
     */
    function resendInvitationLink(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('resendInvitationLink()', body);
            return Post(urls.identity.users.v2, Object.assign(Object.assign({}, body), { provider: 'local' }));
        });
    }

    var teams = /*#__PURE__*/Object.freeze({
        __proto__: null,
        getProfile: getProfile,
        updateProfile: updateProfile,
        updateProfileImage: updateProfileImage,
        changePassword: changePassword,
        loadUsers: loadUsers,
        addUser: addUser,
        deleteUser: deleteUser,
        updateUser: updateUser,
        createInviteUserLink: createInviteUserLink,
        updateInviteUserLink: updateInviteUserLink,
        deleteInviteUserLink: deleteInviteUserLink,
        getInviteUserLink: getInviteUserLink,
        getInviteLinkConfiguration: getInviteLinkConfiguration,
        loadAvailableRoles: loadAvailableRoles,
        loadAvailablePermissions: loadAvailablePermissions,
        loadStats: loadStats,
        resendActivationLink: resendActivationLink,
        resendInvitationLink: resendInvitationLink
    });

    function getMetadata(body) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield Get('/metadata', body);
            if ((_a = data === null || data === void 0 ? void 0 : data.rows) === null || _a === void 0 ? void 0 : _a[0])
                return (_b = data === null || data === void 0 ? void 0 : data.rows) === null || _b === void 0 ? void 0 : _b[0];
            throw new Error(`metadata not found: ${body.entityName}`);
        });
    }
    function getIpMetadata(ip) {
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield Get(`/metadata/ip/${ip}`);
            if (data)
                return data;
            throw new Error(`ip metadata not found`);
        });
    }
    const getNotificationsMetadata = () => __awaiter(void 0, void 0, void 0, function* () { return getMetadata({ entityName: 'notifications' }); });
    const getSamlMetadata = () => __awaiter(void 0, void 0, void 0, function* () { return getMetadata({ entityName: 'saml' }); });
    const getAuditsMetadata = () => __awaiter(void 0, void 0, void 0, function* () { return getMetadata({ entityName: 'audits' }); });
    const getIpAdressMetadata = (ip) => __awaiter(void 0, void 0, void 0, function* () { return getIpMetadata(ip); });

    var metadata = /*#__PURE__*/Object.freeze({
        __proto__: null,
        getNotificationsMetadata: getNotificationsMetadata,
        getSamlMetadata: getSamlMetadata,
        getAuditsMetadata: getAuditsMetadata,
        getIpAdressMetadata: getIpAdressMetadata
    });

    /**
     * Get all active tenant reports from reports service by active vendor.
     * Optional: pass filters and sort options to reports.
     *
     * @returns list of reports objects
     */
    function getReports(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getReports()', body);
            return Get(`${urls.reports.service.v2}/tenant-reports`, body);
        });
    }
    /**
     * Get report record by id.
     *
     * @returns list of report object
     * @throws exception if report not found.
     */
    function getReport(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getReport()', body);
            return Get(`${urls.reports.service.v2}/${body.id}/tenant-reports`);
        });
    }
    /**
     * Render report by id.
     * Optional: pass data filters if exists to render report by specific data.
     *
     * @returns html content of the rendered reports
     * @throws exception if report not found or the data api unreached.
     */
    function renderReport(_a) {
        var { dataFilters } = _a, body = __rest(_a, ["dataFilters"]);
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('renderReport()', body);
            const html = yield Get(`${urls.reports.trigger.v1}/preview-report`, Object.assign(Object.assign({}, body), { dataFilters: btoa(JSON.stringify(dataFilters || {})), responseType: 'html' }), { responseType: 'plain' });
            return { html };
        });
    }
    /**
     * schedule report by providing cron expression and data filters.
     *
     * @returns the updated report object
     * @throws exception if report not found or the data api unreached.
     */
    function scheduleReport(_a) {
        var { templateId, dataFilters } = _a, body = __rest(_a, ["templateId", "dataFilters"]);
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('scheduleReport()', body);
            return Post(`${urls.reports.service.v2}/${templateId}/tenant-reports`, Object.assign(Object.assign({}, body), { dataFilters: btoa(JSON.stringify(dataFilters || {})) }));
        });
    }
    /**
     * download report as file in specific format.
     * this function will add a href element with url to trigger file download.
     * Optional: pass data filters if exists to generate report by specific data.
     *
     * @throws exception if report not found or the data api unreached.
     */
    function downloadReport(_a) {
        var { dataFilters } = _a, body = __rest(_a, ["dataFilters"]);
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('downloadReport()', body);
            const blob = yield Get(`${urls.reports.trigger.v1}/preview-report`, Object.assign(Object.assign({}, body), { dataFilters: btoa(JSON.stringify(dataFilters || {})) }), { responseType: 'blob' });
            let contentType = 'text/html';
            let contentExt = 'html';
            if (body.responseType === 'pdf') {
                contentType = 'application/pdf';
                contentExt = 'pdf';
            }
            else if (body.responseType === 'image') {
                contentType = 'image/jpeg';
                contentExt = 'jpg';
            }
            const newBlob = new Blob([blob], { type: contentType });
            const fileURL = URL.createObjectURL(newBlob);
            const tempLink = document.createElement('a');
            tempLink.href = fileURL;
            tempLink.setAttribute('download', `report_${body.name || ''}.${contentExt}`);
            tempLink.click();
            setTimeout(tempLink.remove.bind(tempLink));
        });
    }
    /**
     * send report to specific email address.
     * Optional: pass data filters if exists to send report by specific data.
     *
     * @throws exception if report not found or the data api unreached.
     */
    function sendReport(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('sendReport()', body);
            return Post(`${urls.reports.trigger.v1}/tenant-reports`, body);
        });
    }

    var reports = /*#__PURE__*/Object.freeze({
        __proto__: null,
        getReports: getReports,
        getReport: getReport,
        renderReport: renderReport,
        scheduleReport: scheduleReport,
        downloadReport: downloadReport,
        sendReport: sendReport
    });

    /**
     * get Notifications
     */
    function getNotifications(params) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getNotifications()');
            return Get(urls.notifications.v1, { params });
        });
    }
    /**
     * update Notification status
     */
    function updateNotificationStatus(params) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getNotifications()');
            return Put(`${urls.notifications.v1}/status`, { params });
        });
    }
    /**
     * update Notification pin status
     */
    function updateNotificationIsPinned(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { notificationId, pinStatus } = params;
            console.debug('getNotifications()');
            return Put(`${urls.notifications.v1}/${pinStatus}`, { notificationId });
        });
    }
    /**
     * mark all notifications as read
     */
    function markAllAsRead() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getNotifications()');
            return Post(`${urls.notifications.v1}/status/mark-all-read`);
        });
    }

    var notifications = /*#__PURE__*/Object.freeze({
        __proto__: null,
        getNotifications: getNotifications,
        updateNotificationStatus: updateNotificationStatus,
        updateNotificationIsPinned: updateNotificationIsPinned,
        markAllAsRead: markAllAsRead
    });

    /*****************************************
     * Audits Api
     *****************************************/
    /**
     * get Audits
     */
    function getAudits(params) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getAudits()');
            return Get(urls.audits.v1, params);
        });
    }
    /**
     * get Audits Stats
     */
    function getAuditsStats(params) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getAuditsStats()');
            return Get(`${urls.audits.v1}/stats`, params);
        });
    }
    /**
     * export Audits
     */
    function exportAudits(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { endpoint, headerProps } = params, restParams = __rest(params, ["endpoint", "headerProps"]);
            return PostDownload(`${urls.audits.v1}/export/${endpoint}`, { properties: headerProps }, restParams);
        });
    }

    var audits = /*#__PURE__*/Object.freeze({
        __proto__: null,
        getAudits: getAudits,
        getAuditsStats: getAuditsStats,
        exportAudits: exportAudits
    });

    const getSlackConfiguration = () => {
        console.debug('getSlackConfiguration()');
        return Get(`${urls.integrations.configurations.v1}/slack`);
    };
    const getSlackScope = () => {
        console.debug('getSlackScope()');
        return Get(`${urls.integrations.configurations.v1}/slack/applications`);
    };
    const getSlackChannels = () => {
        console.debug('getSlackChannels()');
        return Get(`${urls.integrations.configurations.v1}/slack/channels`);
    };
    const postSlackConfiguration = (data) => {
        console.debug('postSlackConfiguration()', data);
        if (data.id) {
            return Patch(`${urls.integrations.configurations.v1}/slack/subscription/${data.id}`, data);
        }
        return Post(`${urls.integrations.configurations.v1}/slack/subscriptions`, data);
    };
    const deleteSlackConfiguration = (data) => {
        console.debug('deleteSlackConfiguration()', data);
        return Delete(`${urls.integrations.configurations.v1}/slack/subscriptions/${data.id}`);
    };
    const postSlackCode = (code) => {
        console.debug('postSlackCode()', { code });
        return Post(`${urls.integrations.configurations.v1}/slack/applications/registrations`, { code });
    };
    const getEmailConfiguration = () => {
        console.debug('getEmailConfiguration()');
        return Get(`${urls.integrations.configurations.v1}/emails`);
    };
    const postEmailConfiguration = (_a) => {
        var { eventKey } = _a, data = __rest(_a, ["eventKey"]);
        console.debug('postEmailConfiguration()', Object.assign(Object.assign({}, data), { eventKey }));
        return Post(`${urls.integrations.configurations.v1}/emails/${eventKey}`, data);
    };
    const patchEmailConfiguration = ({ eventKey, enabled, }) => {
        console.debug('patchEmailConfiguration()', { eventKey, enabled });
        return Patch(`${urls.integrations.configurations.v1}/emails/${eventKey}`, { enabled });
    };
    const deleteEmailSubscriptions = (eventKey, subscriptionId) => {
        console.debug('deleteEmailSubscriptions()', { eventKey, subscriptionId });
        return Delete(`${urls.integrations.configurations.v1}/emails/${eventKey}/subscriptions/${subscriptionId}`);
    };
    const putEmailSubscriptions = (subscriptionId, eventKey, data) => {
        console.debug('putEmailSubscriptions()', Object.assign(Object.assign({}, data), { subscriptionId, eventKey }));
        return Put(`${urls.integrations.configurations.v1}/emails/${eventKey}/subscriptions/${subscriptionId}`, data);
    };
    const deleteEmailConfiguration = (eventKey) => {
        console.debug('deleteEmailConfiguration()', { eventKey });
        return Delete(`${urls.integrations.configurations.v1}/emails/${eventKey}`);
    };
    const getSMSConfiguration = () => {
        console.debug('getSMSConfiguration()');
        return Get(`${urls.integrations.configurations.v2}/sms`);
    };
    const postSMSConfiguration = (_a) => {
        var { eventKey } = _a, data = __rest(_a, ["eventKey"]);
        console.debug('postSMSConfiguration()', Object.assign(Object.assign({}, data), { eventKey }));
        return Post(`${urls.integrations.configurations.v2}/sms/${eventKey}`, data);
    };
    const patchSMSConfiguration = ({ eventKey, enabled, }) => {
        console.debug('patchSMSConfiguration()', { eventKey, enabled });
        return Patch(`${urls.integrations.configurations.v2}/sms/${eventKey}`, { enabled });
    };
    const deleteSMSSubscriptions = (eventKey, subscriptionId) => {
        console.debug('deleteSMSSubscriptions()', { eventKey, subscriptionId });
        return Delete(`${urls.integrations.configurations.v2}/sms/${eventKey}/subscriptions/${subscriptionId}`);
    };
    const putSMSSubscriptions = (subscriptionId, eventKey, data) => {
        console.debug('putSMSSubscriptions()', Object.assign(Object.assign({}, data), { subscriptionId, eventKey }));
        return Put(`${urls.integrations.configurations.v2}/sms/${eventKey}/subscriptions/${subscriptionId}`, data);
    };
    const deleteSMSConfiguration = (eventKey) => {
        console.debug('deleteSMSConfiguration()', { eventKey });
        return Delete(`${urls.integrations.configurations.v2}/sms/${eventKey}`);
    };
    const getWebhooksConfigurations = () => {
        console.debug('getWebhooksConfigurations()');
        return Get(urls.webhooks.v1);
    };
    const postWebhooksConfiguration = (data) => {
        console.debug('postWebhooksConfiguration()', data);
        if (data._id) {
            return Patch(`${urls.webhooks.v1}/${data._id}`, data);
        }
        else {
            return Post(`${urls.webhooks.v1}/custom`, data);
        }
    };
    const deleteWebhooksConfiguration = (id) => {
        console.debug('deleteWebhooksConfiguration()', id);
        return Delete(`${urls.webhooks.v1}/${id}`);
    };
    const getWebhookLog = (id, offset = 0, limit = 10) => {
        console.debug('getWebhookLog()', id);
        const query = new URLSearchParams({ id, offset: `${offset}`, limit: `${limit}` });
        return Get(`${urls.webhooks.v1}/logs/?${query.toString()}`);
    };
    const postWebhookTest = (data) => {
        console.debug('postWebhookTest', data);
        return Post(`${urls.webhooks.v1}/test`, data);
    };
    const postWebhookRetry = (id) => {
        console.debug('postWebhookRetry', id);
        return Post(`${urls.webhooks.v1}/logs/${id}/retries`);
    };
    const getCategories = () => {
        console.debug('getCategories()');
        return Get(`${urls.events.configurations.v1}/categories`);
    };
    const getChannelMaps = (channels) => {
        console.debug('getChannelMaps()', channels);
        return Get(urls.events.configurations.v1, { channels });
    };

    var connectivity = /*#__PURE__*/Object.freeze({
        __proto__: null,
        getSlackConfiguration: getSlackConfiguration,
        getSlackScope: getSlackScope,
        getSlackChannels: getSlackChannels,
        postSlackConfiguration: postSlackConfiguration,
        deleteSlackConfiguration: deleteSlackConfiguration,
        postSlackCode: postSlackCode,
        getEmailConfiguration: getEmailConfiguration,
        postEmailConfiguration: postEmailConfiguration,
        patchEmailConfiguration: patchEmailConfiguration,
        deleteEmailSubscriptions: deleteEmailSubscriptions,
        putEmailSubscriptions: putEmailSubscriptions,
        deleteEmailConfiguration: deleteEmailConfiguration,
        getSMSConfiguration: getSMSConfiguration,
        postSMSConfiguration: postSMSConfiguration,
        patchSMSConfiguration: patchSMSConfiguration,
        deleteSMSSubscriptions: deleteSMSSubscriptions,
        putSMSSubscriptions: putSMSSubscriptions,
        deleteSMSConfiguration: deleteSMSConfiguration,
        getWebhooksConfigurations: getWebhooksConfigurations,
        postWebhooksConfiguration: postWebhooksConfiguration,
        deleteWebhooksConfiguration: deleteWebhooksConfiguration,
        getWebhookLog: getWebhookLog,
        postWebhookTest: postWebhookTest,
        postWebhookRetry: postWebhookRetry,
        getCategories: getCategories,
        getChannelMaps: getChannelMaps
    });

    /**
     * switch logged in user to specific tenant by providing tenantId.
     *
     * @throws exception if invalid tenantId.
     * ``authorized user``
     */
    function switchTenant(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('switchTenant()', body);
            return Put(`${urls.identity.users.v1}/tenant`, body);
        });
    }
    /**
     * retrieve logged in user's available tenants.
     *
     * ``authorized user``
     */
    function getTenants() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getTenants()');
            return Get(`${urls.identity.users.v2}/me/tenants`);
        });
    }

    var tenants = /*#__PURE__*/Object.freeze({
        __proto__: null,
        switchTenant: switchTenant,
        getTenants: getTenants
    });

    /*** update account settings for tenant.
     * tenantId is caclulated in the backend from context
     * ``authorized user``
     */
    function updateSettings(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateSettings()', body);
            return Put(urls.tenants.accountSettings.v1, body);
        });
    }
    function getSettings() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSettings()');
            return Get(urls.tenants.accountSettings.v1);
        });
    }

    var accountSettings = /*#__PURE__*/Object.freeze({
        __proto__: null,
        updateSettings: updateSettings,
        getSettings: getSettings
    });

    /**
     * Gets tenant related roles
     */
    function getRoles() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getRoles()');
            return Get(urls.identity.roles.v1);
        });
    }
    /**
     * Adds tenant related roles
     * @param body: IAddRole
     */
    function addRole(body) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('addRole()', body);
            return Post(urls.identity.roles.v1, body);
        });
    }
    /**
     * Deletes role given role ID
     */
    function deleteRole({ roleId }) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('deleteRole()', roleId);
            return Delete(`${urls.identity.roles.v1}/${roleId}`);
        });
    }
    /**
     * Updates role using a given role ID and IUpdateRole object
     */
    function updateRole(_a) {
        var { roleId } = _a, body = __rest(_a, ["roleId"]);
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('updateRole()', roleId, body);
            return Patch(`${urls.identity.roles.v1}/${roleId}`, body);
        });
    }
    /**
     * Attaches permissions to role given a role ID
     */
    function attachPermissionsToRole(_a) {
        var { roleId } = _a, body = __rest(_a, ["roleId"]);
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('attachPermissionsToRole()', roleId, body);
            return Put(`${urls.identity.roles.v1}/${roleId}/permissions`, body);
        });
    }
    /**
     * Gets all vendor permissions
     */
    function getPermissions() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getPermissions()');
            return Get(urls.identity.permissions.v1);
        });
    }
    /**
     * Associate permission to roles
     */
    function attachPermissionToRoles(_a) {
        var { permissionId } = _a, body = __rest(_a, ["permissionId"]);
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('attachPermissionToRoles()');
            return Put(`${urls.identity.permissions.v1}/${permissionId}/roles`, body);
        });
    }
    /**
     * Gets all vendor categories including the permissions
     */
    function getPermissionCategories() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getPermissionCategories()');
            return Get(`${urls.identity.permissions.v1}/categories`);
        });
    }

    var roles = /*#__PURE__*/Object.freeze({
        __proto__: null,
        getRoles: getRoles,
        addRole: addRole,
        deleteRole: deleteRole,
        updateRole: updateRole,
        attachPermissionsToRole: attachPermissionsToRole,
        getPermissions: getPermissions,
        attachPermissionToRoles: attachPermissionToRoles,
        getPermissionCategories: getPermissionCategories
    });

    /**
     * Get tenant subscriptions
     */
    function getSubscriptions() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("getSubscriptions()");
            return Get(urls.subscriptions.billing.subscriptions.v1);
        });
    }
    /**
     * Get tenant subscription
     */
    function getSubscription(subscriptionId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("getSubscription()", { subscriptionId });
            return Get(`${urls.subscriptions.billing.subscriptions.v1}/${subscriptionId}`);
        });
    }
    /**
     * Cancel tenant subscription
     */
    function cancelSubscription(subscriptionId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("cancelSubscription()", { subscriptionId });
            return Put(`${urls.subscriptions.billing.subscriptions.v1}/${subscriptionId}/cancellations/`, {});
        });
    }
    /**
     * Renew tenant subscription
     */
    function renewSubscription(subscriptionId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("renewSubscription()", { subscriptionId });
            return Put(`${urls.subscriptions.billing.subscriptions.v1}/${subscriptionId}/renewals/`, {});
        });
    }
    /**
     * Upgrade/Downgrade tenant subscription plan
     */
    function updateSubscription(subscriptionId, { paymentMethodId, items }) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("updateSubscription()", { subscriptionId });
            return Put(`${urls.subscriptions.billing.subscriptions.v1}/${subscriptionId}`, { paymentMethodId, items });
        });
    }

    /**
     * Gets subscription plans
     */
    function getSubscriptionPlans() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSubscriptionPlans()');
            return Get(urls.subscriptions.billing.plans.v1);
        });
    }
    /**
     * Gets subscription plan singular
     */
    function getSubscriptionPlan(planId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSubscriptionPlan()');
            return Get(`${urls.subscriptions.billing.plans.v1}/${planId}`);
        });
    }

    /**
     * Get subscription invoices
     */
    function getSubscriptionInvoices() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("getSubscriptionInvoices()");
            return Get(urls.subscriptions.billing.invoices.v1);
        });
    }
    /**
     * Get subscription invoice
     */
    function getSubscriptionInvoice(invoiceId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("getSubscriptionInvoice()", { invoiceId });
            return Get(`${urls.subscriptions.billing.invoices.v1}/${invoiceId}`);
        });
    }
    /**
     * Get subscription invoice pdf file
     */
    function getSubscriptionInvoicePdf(invoiceId, outputFileName) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("getSubscriptionInvoicePdf()", { invoiceId });
            return Get(`${urls.subscriptions.billing.invoices.v1}/${invoiceId}/pdf`, {
                outputFileName,
            }, {
                responseType: "blob",
            });
        });
    }

    /**
     * Get payment methods
     */
    function getPaymentMethods() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("getPaymentMethods()");
            return Get(urls.subscriptions.billing.paymentMethods.v1);
        });
    }
    /**
     * Get payment method
     */
    function getPaymentMethod(paymentMethodId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("getPaymentMethod()", { paymentMethodId });
            return Get(`${urls.subscriptions.billing.paymentMethods.v1}/${paymentMethodId}`);
        });
    }
    /**
     * Update payment method billing details
     */
    function updatePaymentMethodBillingDetails(paymentMethodId, _a) {
        var { email } = _a, address = __rest(_a, ["email"]);
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("updatePaymentMethodBillingDetails()", {
                paymentMethodId,
                email,
                address,
            });
            return Patch(`${urls.subscriptions.billing.paymentMethods.v1}/${paymentMethodId}/billing-details`, { email, address });
        });
    }

    /**
     * Creates new stripe subscription
     * @param request
     */
    function createSubscription(request) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('createSubscription()');
            return Post(urls.subscriptions.billing.paymentProviders.stripe.subscriptions.v1, request);
        });
    }
    /**
     * Fetch tenant mapping external stripe customer object
     */
    function getStripeCustomer(tenantId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getStripeCustomer()');
            return Get(`${urls.subscriptions.billing.paymentProviders.stripe.customers.v1}/${tenantId}`);
        });
    }
    /**
     * Create tenant mapping external stripe customer object
     * @param request
     */
    function createStripeCustomer(request) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('createStripeCustomer()');
            return Post(`${urls.subscriptions.billing.paymentProviders.stripe.customers.v1}`, request);
        });
    }
    function getStripePaymentProviderConfiguration() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getStripePaymentProviderConfiguration()');
            return Get(`${urls.subscriptions.billing.paymentProviders.stripe.publicConfigurations.v1}`);
        });
    }
    /**
     * Create payment method intent secret
     * @param request
     */
    function createStripePaymentMethodSetupIntentSecret(request) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('createStripePaymentMethodSetupIntentSecret()');
            return Post(`${urls.subscriptions.billing.paymentProviders.stripe.setupIntents.v1}`, request);
        });
    }

    /**
     * Gets subscription summaries
     */
    function getSubscriptionSummaries(tenantId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getSubscriptionSummaries()');
            return Get(`${urls.subscriptions.billing.summaries.v1}/${tenantId}`);
        });
    }

    function getTenantConfiguration(tenantId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("getTenantConfiguration()");
            return Get(`${urls.subscriptions.billing.tenantConfiguration.v1}/${tenantId}`);
        });
    }
    function createTenantConfiguration(request) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("createTenantConfiguration()");
            return Post(`${urls.subscriptions.billing.tenantConfiguration.v1}`, request);
        });
    }

    /**
     * Gets payment providers for vendor
     */
    function getPaymentProviders() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("getPaymentProviders()");
            return Get(urls.subscriptions.paymentProviders.v1);
        });
    }

    exports.ISubscriptionStatus = void 0;
    (function (ISubscriptionStatus) {
        ISubscriptionStatus["ACTIVE"] = "active";
        ISubscriptionStatus["CANCELED"] = "canceled";
        ISubscriptionStatus["INCOMPLETE"] = "incomplete";
        ISubscriptionStatus["EXPIRED"] = "expired";
    })(exports.ISubscriptionStatus || (exports.ISubscriptionStatus = {}));
    exports.PaymentMethodType = void 0;
    (function (PaymentMethodType) {
        PaymentMethodType["UNKNWON"] = "unknown";
        PaymentMethodType["CARD"] = "card";
    })(exports.PaymentMethodType || (exports.PaymentMethodType = {}));
    exports.ISubscriptionCancellationPolicy = void 0;
    (function (ISubscriptionCancellationPolicy) {
        ISubscriptionCancellationPolicy["AT_PERIOD_END"] = "atPeriodEnd";
    })(exports.ISubscriptionCancellationPolicy || (exports.ISubscriptionCancellationPolicy = {}));
    exports.ProviderType = void 0;
    (function (ProviderType) {
        ProviderType["Stripe"] = "Stripe";
    })(exports.ProviderType || (exports.ProviderType = {}));

    var subscriptions = /*#__PURE__*/Object.freeze({
        __proto__: null,
        getSubscriptions: getSubscriptions,
        getSubscription: getSubscription,
        cancelSubscription: cancelSubscription,
        renewSubscription: renewSubscription,
        updateSubscription: updateSubscription,
        getSubscriptionPlans: getSubscriptionPlans,
        getSubscriptionPlan: getSubscriptionPlan,
        getSubscriptionInvoices: getSubscriptionInvoices,
        getSubscriptionInvoice: getSubscriptionInvoice,
        getSubscriptionInvoicePdf: getSubscriptionInvoicePdf,
        getPaymentMethods: getPaymentMethods,
        getPaymentMethod: getPaymentMethod,
        updatePaymentMethodBillingDetails: updatePaymentMethodBillingDetails,
        createSubscription: createSubscription,
        getStripeCustomer: getStripeCustomer,
        createStripeCustomer: createStripeCustomer,
        getStripePaymentProviderConfiguration: getStripePaymentProviderConfiguration,
        createStripePaymentMethodSetupIntentSecret: createStripePaymentMethodSetupIntentSecret,
        getSubscriptionSummaries: getSubscriptionSummaries,
        getTenantConfiguration: getTenantConfiguration,
        createTenantConfiguration: createTenantConfiguration,
        getPaymentProviders: getPaymentProviders,
        get ISubscriptionStatus () { return exports.ISubscriptionStatus; },
        get PaymentMethodType () { return exports.PaymentMethodType; },
        get ISubscriptionCancellationPolicy () { return exports.ISubscriptionCancellationPolicy; },
        get ProviderType () { return exports.ProviderType; }
    });

    /**
     * provide vendor public info by providing the vendor context.
     * @return IVendorPublicInfoResponse
     */
    function getVendorPublicInfo() {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug('getVendorPublicInfo()');
            return Get(`${urls.vendor}/public`);
        });
    }

    var vendor = /*#__PURE__*/Object.freeze({
        __proto__: null,
        getVendorPublicInfo: getVendorPublicInfo
    });

    const api = {
        auth,
        teams,
        metadata,
        reports,
        connectivity,
        notifications,
        audits,
        tenants,
        accountSettings,
        roles,
        subscriptions,
        vendor
    };
    var index = {
        fetch: fetch$1,
        ContextHolder,
        FronteggContext,
        api,
        FronteggApiError,
        AuthStrategyEnum: exports.AuthStrategyEnum,
        SocialLoginProviders: exports.SocialLoginProviders,
        ISubscriptionCancellationPolicy: exports.ISubscriptionCancellationPolicy,
        ISubscriptionStatus: exports.ISubscriptionStatus,
        PaymentMethodType: exports.PaymentMethodType,
        ProviderType: exports.ProviderType,
    };

    exports.ContextHolder = ContextHolder;
    exports.FronteggApiError = FronteggApiError;
    exports.FronteggContext = FronteggContext;
    exports.api = api;
    exports.default = index;
    exports.fetch = fetch$1;

    Object.defineProperty(exports, '__esModule', { value: true });

})));
